import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions, user as authUser } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import WorstScoresTableCard from '@/_legacy/views/Analytics/Overview/WorstScoresTableCard.vue';
import { QuestionGroupBy } from '@/services/api/resources/analytics/types';
import {
  groupedQuestionList,
  groupedScoresListExportUrl,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { handleScoreClick, redirectScores } from '@/ui/pages/analytics/Overview/redirects';
import { useTask } from '@/ui/concerns';
import GroupTitleDecoder from '@/_legacy/views/Analytics/decoders/GroupTitleDecoder';
import { download } from '@/ui/helpers/uiHelper';
import { useQueries } from '@/ui/pages/analytics/Overview/useQueries';
import { parseInt10 } from '@/ui/helpers';

export const WorstScoresCard = defineComponent({
  name: 'WorstScoresCard',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    const groupWorstScores = ref<QuestionGroupBy>(QuestionGroupBy.Question);

    const {
      worstScoresTableQuery,
    } = useQueries();

    const tableList = ref<unknown>([]);

    const getList = useTask(
      () => {
        initialLoading.value = true;

        return groupedQuestionList(groupWorstScores.value, worstScoresTableQuery());
      },
      tableResponse => {
        tableList.value = tableResponse.data.map(result => ({
          label: GroupTitleDecoder(result.group),
          group: result.group,
          value: result.scorePercentage,
        }));

        initialLoading.value = false;
      },
    );
    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedScoresListExportUrl(
        groupWorstScores.value,
        { ...worstScoresTableQuery(), perPage: parseInt10(process.env.VUE_APP_MAX_ANALYTICS_EXPORT!) },
        format as SupportedExportFormat,
        authUser.value.locale,
      ),
      download,
    );

    watch([
      () => worstScoresTableQuery(),
      groupWorstScores,
    ], getList.run, { deep: true });

    getList.run();

    return function render() {
      //legacy, not compatible with Vue typings
      //@ts-ignore
      return h(WorstScoresTableCard, {
        props: {
          results: tableList.value,
          loading: initialLoading.value,
          groupBy: groupWorstScores.value,
        },
        on: {
          click: () => redirectScores(worstScoresTableQuery()),
          groupByChanged: (value: QuestionGroupBy) => {
            groupWorstScores.value = value;
          },
          exportClicked: (format: SupportedExportFormat) => exportData(format),
          rowClick: (
            { row, groupBy }: { row: { group: { id: number } }; groupBy: QuestionGroupBy },
          ) => handleScoreClick(worstScoresTableQuery(), row, groupBy),
        },
      });
    };
  },
});
